import { useCallback, useEffect, useRef, useState } from 'react';
import { api, money } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';
import { formatQuantity, parseQuantity, quantityError } from '../lib/quantity.js';
import {
  discard, enqueueCount, failedForTake, flushQueue, lineKey, pendingByLine, queued,
} from '../lib/countQueue.js';
import { answerFor, countedCount, hasExpected, isUncounted, showsExpected } from '../lib/stockSheet.js';

/**
 * The count sheet — the screen that matters in this module.
 *
 * Everything else in stock is an arithmetic identity between numbers the system
 * produced itself. The count is the first time the books are checked against the
 * room, and it happens on a phone in a storeroom with no signal, held by
 * somebody two hours into a job they would rather have finished. So:
 *
 * 1. NOTHING IS SENT DIRECTLY. Every counted figure goes into IndexedDB first
 *    (countQueue.js) and the network is treated as something that might not be
 *    there. What is waiting, and what was refused and why, is shown on the
 *    screen — a count that silently failed to upload is a count thrown away.
 *
 * 2. THE SHEET IS BLIND UNLESS THE RESPONSE SAYS OTHERWISE. A holder of
 *    `stock.count` without `stock.view` — which is exactly a `client_user`, the
 *    person who actually walks the shelves — gets lines where `expectedQty` is
 *    NOT A KEY AT ALL. Not null, ABSENT. This screen therefore renders what the
 *    response contains and asks `'expectedQty' in line`, never `!= null`, and
 *    never defaults a missing key to 0. A counter who can see the expected
 *    figure counts to it, and the number they write down is the number that was
 *    already there. The API went to real trouble to remove that field; a
 *    helpful `?? 0` here would hand it straight back.
 *
 * 3. THERE IS NO "MARK THE REST AS ZERO" BUTTON, ANYWHERE. Not behind a
 *    confirm, not behind an "are you sure". A tired person tapping through a
 *    dialog at six o'clock must not be able to write off everything nobody
 *    reached. The API offers `uncounted: 'refuse' | 'leave'` and deliberately
 *    has no third option; this screen must not manufacture one by looping the
 *    zero write itself. If you are here to add that button, the answer is no.
 *
 * 4. A BLANK IS NOT A ZERO. An uncounted line reads `—`; a line counted at
 *    nothing reads `0`. They are different facts about the world, and the whole
 *    module refuses to collapse them at every layer.
 */

const STATUS_LABEL = {
  open: 'Open — being counted',
  posted: 'Posted',
  abandoned: 'Abandoned',
};

export function StockTake({ entity }) {
  const caps = entity?.capabilities ?? [];
  // `stock.count.post` is what it takes to review a variance and post it. A
  // counting client_user does not hold it, so the screen has two distinct modes
  // and the counter never sees a post button at all.
  const canPost = caps.includes('stock.count.post');

  const [takes, setTakes] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const loadTakes = useCallback(() => api.get(`/api/entities/${entity.id}/stock-takes`)
    .then((d) => setTakes(d.stockTakes))
    .catch((e) => { setErr(e.message); setTakes([]); }), [entity?.id]);

  useEffect(() => { if (entity?.id) loadTakes(); }, [entity?.id, loadTakes]);

  if (openId) {
    return (
      <Sheet entity={entity} takeId={openId} canPost={canPost}
             onClose={() => { setOpenId(null); loadTakes(); }} />
    );
  }

  return (
    <>
      <ReportHead entity={entity} title="Stock take"
                  sub="Check the books against the room. Counts are kept on this phone until they send." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <OpenSheet entity={entity} canPost={canPost}
                 onOpened={(take) => { setMsg(`Count sheet ${take.number} opened.`); setOpenId(take.id); }}
                 onError={setErr} />

      {takes === null ? (
        <div className="empty">Loading…</div>
      ) : takes.length === 0 ? (
        <Empty>
          <p>No count sheets yet.</p>
          <p>Open one on the room you are about to count.</p>
        </Empty>
      ) : (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Sheet</th>
                <th style={{ width: 110 }}>Date</th>
                <th>Location</th>
                <th style={{ width: 180 }}>Status</th>
                <th className="num" style={{ width: 130 }}>Counted</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {takes.map((t) => (
                <tr key={t.id}>
                  <td className="code figure">{t.number}</td>
                  <td className="figure">{t.countDate}</td>
                  <td><span className="code figure">{t.locationCode}</span> {t.locationName}</td>
                  <td>
                    {STATUS_LABEL[t.status] ?? t.status}
                    {t.status === 'posted' && !t.entryId && (
                      <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}> · no journal</span>
                    )}
                  </td>
                  <td className="num figure">
                    {t.lineCount === undefined ? '—' : `${t.countedCount} of ${t.lineCount}`}
                  </td>
                  <td className="num">
                    <button className="icon-btn" onClick={() => setOpenId(t.id)}>Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Open a sheet on one room. One sheet per location at a time, and the API holds that. */
function OpenSheet({ entity, canPost, onOpened, onError }) {
  const today = new Date().toISOString().slice(0, 10);
  const [locations, setLocations] = useState(null);
  const [form, setForm] = useState({ locationId: '', countDate: today, notes: '', kinds: '' });
  const [busy, setBusy] = useState(false);
  const [showing, setShowing] = useState(false);

  useEffect(() => {
    if (!entity?.id) return;
    api.get(`/api/entities/${entity.id}/stock-locations?active=true`)
      .then((d) => {
        setLocations(d.locations);
        const def = d.locations.find((l) => l.isDefault) ?? d.locations[0];
        if (def) setForm((f) => ({ ...f, locationId: def.id }));
      })
      // Listing locations needs `stock.view`, which a counting client_user does
      // not hold. That is not an error to shout about: it means somebody else
      // opens the sheet and this person counts it.
      .catch(() => setLocations([]));
  }, [entity?.id]);

  const open = async () => {
    setBusy(true);
    try {
      const body = { locationId: form.locationId, countDate: form.countDate };
      if (form.notes.trim()) body.notes = form.notes.trim();
      if (form.kinds) body.scope = { kinds: [form.kinds] };
      const { stockTake } = await api.post(`/api/entities/${entity.id}/stock-takes`, body);
      onOpened(stockTake);
    } catch (e) { onError(e.message); } finally { setBusy(false); }
  };

  if (locations === null) return null;
  if (locations.length === 0) {
    return (
      <div className="note">
        Open the sheet from a device that can see the stock locations, then count it here.
      </div>
    );
  }

  if (!showing) {
    return (
      <button className="btn" style={{ margin: '18px 0' }} onClick={() => setShowing(true)}>
        Open a count sheet
      </button>
    );
  }

  return (
    <div style={{ margin: '18px 0' }}>
      <div className="form-grid">
        <label className="field" style={{ minWidth: 220 }}><span>Location</span>
          <select value={form.locationId} onChange={(e) => setForm({ ...form, locationId: e.target.value })}>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
          </select>
        </label>
        <label className="field"><span>Count date</span>
          <input type="date" value={form.countDate}
                 onChange={(e) => setForm({ ...form, countDate: e.target.value })} /></label>
        <label className="field" style={{ minWidth: 180 }}><span>Only</span>
          <select value={form.kinds} onChange={(e) => setForm({ ...form, kinds: e.target.value })}>
            <option value="">Everything stocked here</option>
            <option value="quantified">Quantified items only</option>
            <option value="individual">Individual items only</option>
          </select>
        </label>
        <label className="field" style={{ minWidth: 240 }}><span>Notes</span>
          <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn" onClick={open} disabled={busy || !form.locationId}>
          {busy ? 'Opening' : 'Open the sheet'}
        </button>
        <button className="btn btn--quiet" onClick={() => setShowing(false)}>Cancel</button>
      </div>
      <div className="note">
        Only one sheet may be open on a location at a time — two sheets on one room both freeze
        the same expected figures, and posting both writes the same difference off twice.
        {canPost ? '' : ' The sheet is reviewed and posted by your bookkeeper once you have counted it.'}
      </div>
    </div>
  );
}

/** One sheet, being counted. */
function Sheet({ entity, takeId, canPost, onClose }) {
  const [sheet, setSheet] = useState(null);
  const [pending, setPending] = useState(new Map());
  const [failed, setFailed] = useState([]);
  const [drafts, setDrafts] = useState({});      // line id -> what is in the box
  const [onlyUncounted, setOnlyUncounted] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);    // the variance preview
  const [posted, setPosted] = useState(null);

  const loadSheet = useCallback(() => api.get(`/api/stock-takes/${takeId}`)
    .then((d) => setSheet(d.stockTake))
    .catch((e) => setErr(e.message)), [takeId]);

  const refreshQueue = useCallback(async () => {
    const all = await queued();
    setPending(pendingByLine(all, takeId));
    setFailed(failedForTake(all, takeId));
  }, [takeId]);

  // ONE PASS AT A TIME. Mount, the `online` event, the button and every count
  // all flush, and two overlapping passes would post the same queued count
  // twice — harmless server-side, since it deduplicates on clientRef, but it
  // widens the window in which a record is in flight for no gain.
  const flushing = useRef(false);
  const flush = useCallback(async () => {
    if (flushing.current) return { sent: 0, failed: 0 };
    flushing.current = true;
    try {
      const result = await flushQueue(api, takeId);
      if (result.sent > 0) await loadSheet();
      return result;
    } finally {
      flushing.current = false;
      await refreshQueue();
    }
  }, [takeId, loadSheet, refreshQueue]);

  // A page load is itself a good moment to look for signal, and so is the
  // browser telling us it is back.
  useEffect(() => { loadSheet(); refreshQueue(); flush(); }, [takeId]); // eslint-disable-line
  useEffect(() => {
    const onOnline = () => flush();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [flush]);

  /**
   * Record one counted figure.
   *
   * The quantity is parsed HERE, before anything is queued, so a mis-key is
   * refused while the counter is still looking at the field — not three hours
   * later as a failed queue item they cannot correct from a shelf.
   */
  const count = async (line, typed) => {
    setErr(null); setMsg(null); setBusy(true);
    try {
      const countedQty = parseQuantity(typed);
      await enqueueCount({
        // stockTakeId travels WITH the count. The queue outlives this screen.
        stockTakeId: takeId,
        itemId: line.itemId,
        serialNo: line.serialNo ?? null,
        countedQty,
      });
      setDrafts((d) => ({ ...d, [line.id]: '' }));
      await refreshQueue();
      const { sent, failed: refused } = await flush();
      if (refused > 0) setMsg('Saved. One of your counts was refused — see below.');
      else if (sent > 0) setMsg(`${line.code} recorded.`);
      else setMsg('Saved on this device — it will send once you have signal.');
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  // The failed list on screen is a snapshot. By the time somebody taps Remove
  // the counter may have recounted that line, and the replacement sits at the
  // same key — so the ref goes with the removal and only the refused record is
  // taken. See dropSent in countQueue.js.
  const removeFailed = async (f) => { await discard(f.key, f.clientRef); await refreshQueue(); };

  const runReview = async (adjustmentAccountId) => {
    setErr(null); setBusy(true);
    try {
      const q = adjustmentAccountId ? `?adjustmentAccountId=${adjustmentAccountId}` : '';
      setReview(await api.get(`/api/stock-takes/${takeId}/variances${q}`));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const post = async ({ uncounted, adjustmentAccountId }) => {
    setErr(null); setBusy(true);
    try {
      const body = { uncounted };
      if (adjustmentAccountId) body.adjustmentAccountId = adjustmentAccountId;
      const result = await api.post(`/api/stock-takes/${takeId}/post`, body);
      setPosted(result);
      setReview(null);
      await loadSheet();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const abandon = async () => {
    setErr(null); setBusy(true);
    try {
      await api.post(`/api/stock-takes/${takeId}/abandon`, {});
      await loadSheet();
      setMsg('This sheet has been abandoned. Nothing was posted.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (!sheet) {
    return (
      <>
        {err && <div className="error">{err}</div>}
        <div className="empty">{err ? '' : 'Loading…'}</div>
        <button className="btn btn--quiet" onClick={onClose}>Back to the sheets</button>
      </>
    );
  }

  // Which line a queued count answers. The serial is part of it: one individual
  // item is twenty lines on a sheet, one per machine.
  const keyOf = (line) =>
    lineKey({ stockTakeId: takeId, itemId: line.itemId, serialNo: line.serialNo });

  // Whether this response carries the expected figures at all — the KEY's
  // presence and never its value. See lib/stockSheet.js, where the rule lives
  // so that it can be tested, and where the reasoning is written out.
  const sighted = showsExpected(sheet.lines);

  const isOpen = sheet.status === 'open';
  const counted = countedCount(sheet.lines, pending, keyOf);
  const visible = onlyUncounted
    ? sheet.lines.filter((l) => isUncounted(l, pending, keyOf))
    : sheet.lines;
  const waiting = [...pending.values()].filter((p) => !p.failed).length;

  return (
    <>
      <ReportHead entity={entity} title={`Count sheet ${sheet.number}`}
                  sub={`${sheet.locationCode} ${sheet.locationName} · ${sheet.countDate} · ${STATUS_LABEL[sheet.status] ?? sheet.status}`} />

      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      {failed.length > 0 && (
        <div className="error">
          <strong>{failed.length} count{failed.length > 1 ? 's' : ''} could not be sent:</strong>
          <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
            {failed.map((f) => {
              // Matched on the SERIAL as well as the item: one individual item
              // is twenty lines on a sheet, one per machine, and naming the
              // wrong one would send somebody to recount the wrong shelf.
              const line = sheet.lines.find(
                (l) => l.itemId === f.itemId && (l.serialNo ?? null) === (f.serialNo ?? null));
              return (
                <li key={f.key} style={{ marginBottom: 6 }}>
                  {line ? `${line.code}${line.serialNo ? ` ${line.serialNo}` : ''}` : 'A count'}
                  {' '}of {formatQuantity(f.countedQty)} — {f.failedReason ?? 'rejected by the server.'}{' '}
                  <button className="icon-btn" onClick={() => removeFailed(f)}>Remove</button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="controls" style={{ alignItems: 'center', gap: 14 }}>
        <button className="btn btn--quiet" onClick={onClose}>Back to the sheets</button>
        <button className="icon-btn" aria-pressed={onlyUncounted}
                style={onlyUncounted ? { borderColor: 'var(--ink)', color: 'var(--ink)' } : undefined}
                onClick={() => setOnlyUncounted((v) => !v)}>
          {onlyUncounted ? 'Showing not counted' : 'Not counted'}
        </button>
        <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
          {counted} of {sheet.lines.length} counted
        </span>
        {waiting > 0 && (
          <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
            {waiting} waiting to send
          </span>
        )}
        {waiting > 0 && (
          <button className="icon-btn" onClick={flush} disabled={busy}>Try to send now</button>
        )}
      </div>

      {!sighted && isOpen && (
        <div className="note">
          This is a <strong>blind sheet</strong>: it does not show what the books expect, because a
          sheet that prints the expected figure is not a count. Write down what is on the shelf.
        </div>
      )}

      <div className="scroll-x">
        <table className="ledger">
          <thead>
            <tr>
              <th style={{ width: 60 }}>#</th>
              <th style={{ width: 110 }}>Code</th>
              <th>Item</th>
              <th style={{ width: 150 }}>Serial</th>
              {/* Rendered only when the response actually carries them. */}
              {sighted && <th className="num" style={{ width: 110 }}>Expected</th>}
              <th className="num" style={{ width: 110 }}>Counted</th>
              {sighted && <th className="num" style={{ width: 110 }}>Difference</th>}
              {isOpen && <th style={{ width: 230 }}>Count it</th>}
            </tr>
          </thead>
          <tbody>
            {visible.map((line) => {
              const answer = answerFor(line, pending, keyOf);
              return (
                <tr key={line.id}>
                  <td className="figure" style={{ color: 'var(--ink-faint)' }}>{line.lineNo}</td>
                  <td className="code figure">{line.code}</td>
                  <td>{line.name}</td>
                  <td className="figure">{line.serialNo ?? '—'}</td>
                  {sighted && (
                    <td className="num figure">
                      {hasExpected(line) ? formatQuantity(line.expectedQty) : '—'}
                    </td>
                  )}
                  <td className="num figure">
                    {/* formatQuantity(null) is '—'. A blank is not a zero. */}
                    {formatQuantity(answer.qty)}
                    {answer.local && (
                      <span title="on this device, not yet sent"
                            style={{ color: 'var(--ink-faint)' }}> ·</span>
                    )}
                    {/* A REFUSED FIGURE IS MARKED, NOT SHOWN AS THE ANSWER.
                        The line above is what the books hold; this is what the
                        server would not take, and it needs recounting. */}
                    {answer.refused !== null && (
                      <div className="neg" style={{ fontSize: 12 }}
                           title="the server would not accept this — count it again">
                        {formatQuantity(answer.refused)} refused
                      </div>
                    )}
                  </td>
                  {sighted && (
                    <td className="num figure">
                      {line.varianceQty !== null && line.varianceQty !== undefined
                        ? <span className={String(line.varianceQty).startsWith('-') ? 'neg' : ''}>
                            {formatQuantity(line.varianceQty)}
                          </span>
                        : '—'}
                    </td>
                  )}
                  {isOpen && (
                    <td>
                      <CountControl line={line} busy={busy}
                                    draft={drafts[line.id] ?? ''}
                                    onDraft={(v) => setDrafts((d) => ({ ...d, [line.id]: v }))}
                                    onCount={(v) => count(line, v)} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {visible.length === 0 && (
        <Empty><p>Every line on this sheet has been counted.</p></Empty>
      )}

      <div className="note">
        Counts are written to this device the moment you enter them, so losing signal will not lose
        the count — they send themselves as soon as you are back online. A line nobody reached
        stays blank; there is deliberately no way to write the rest of the sheet off to zero.
      </div>

      {canPost && isOpen && (
        <Review entity={entity} sheet={sheet} review={review} busy={busy}
                onRun={runReview} onPost={post} onAbandon={abandon} />
      )}

      {posted && <Posted result={posted} />}

      {sheet.status === 'posted' && !posted && canPost && (
        <PostedSheet takeId={sheet.id} sheet={sheet} />
      )}
    </>
  );
}

/**
 * One input per line — and for an individual item, two buttons instead.
 *
 * AN individual LINE IS 0 OR 1 AND NEVER A FREE NUMBER PAD. The line names one
 * serial number, and the only question about that machine is whether it is in
 * the room. A number pad there invites "3", which is not a fact about SN-0041
 * and which the layer rules would refuse anyway.
 */
function CountControl({ line, draft, busy, onDraft, onCount }) {
  if (line.itemKind === 'individual') {
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="icon-btn" disabled={busy} onClick={() => onCount('1')}>Found</button>
        <button className="icon-btn" disabled={busy} onClick={() => onCount('0')}>Not found</button>
      </div>
    );
  }

  const problem = draft.trim() ? quantityError(draft) : null;
  return (
    <div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="input--figure" value={draft} inputMode="decimal"
               style={{ width: 110, textAlign: 'right' }} placeholder="0"
               onChange={(e) => onDraft(e.target.value)}
               disabled={busy}
               onKeyDown={(e) => {
                 // GUARDED THE SAME WAY THE BUTTON IS. An unguarded Enter is a
                 // second count enqueued while the first is still in flight,
                 // which is precisely the race dropSent exists to survive —
                 // surviving it is not a reason to invite it.
                 if (e.key === 'Enter' && !busy && !problem && draft.trim()) onCount(draft);
               }} />
        <button className="icon-btn" disabled={busy || !draft.trim() || Boolean(problem)}
                onClick={() => onCount(draft)}>
          Record
        </button>
      </div>
      {problem && (
        <div style={{ fontSize: 12, color: 'var(--unbalanced)', marginTop: 4 }}>{problem}</div>
      )}
    </div>
  );
}

/**
 * The variance review, and the post.
 *
 * Gated on `stock.count.post`, which the counting client_user does not hold, so
 * this whole block simply does not render for them. A reviewer deciding whether
 * to post a difference obviously has to see the difference — the blind sheet is
 * a property of the counting routes, not of this one.
 */
function Review({ entity, sheet, review, busy, onRun, onPost, onAbandon }) {
  const [accounts, setAccounts] = useState([]);
  const [contra, setContra] = useState('');
  const [uncounted, setUncounted] = useState('refuse');

  useEffect(() => {
    api.get(`/api/entities/${entity.id}/accounts`).then((d) => setAccounts(d.accounts)).catch(() => {});
  }, [entity?.id]);

  const contras = accounts.filter((a) => a.is_postable && a.is_active
    && ['operating_expense', 'cost_of_sales', 'other_income'].includes(a.subtype));

  return (
    <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--rule-strong)' }}>
      <h2 className="display" style={{ fontSize: 20, margin: '0 0 10px' }}>Review and post</h2>

      <div className="controls">
        <label className="field" style={{ minWidth: 260 }}><span>Write the difference to</span>
          <select value={contra} onChange={(e) => setContra(e.target.value)}>
            <option value="">The stock adjustment account</option>
            {contras.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
          </select>
        </label>
        <button className="btn" onClick={() => onRun(contra)} disabled={busy}>
          {busy ? 'Working' : review ? 'Refresh the preview' : 'Preview what this will post'}
        </button>
        <button className="btn btn--quiet" onClick={onAbandon} disabled={busy}>
          Abandon this sheet
        </button>
      </div>

      {review && (
        <>
          {/* NULL UNLESS THIS COUNT'S JOURNAL HAS SINCE BEEN REVERSED. A sheet
              whose effect was undone must never look like one that stands. */}
          {review.reversal && <ReversalNotice reversal={review.reversal} />}

          {review.variances.length === 0 ? (
            <div className="note">
              <strong>Every counted line agrees with the books.</strong> Posting this sheet gives it
              its number and seals it, and there is no journal to post — which is the most common
              outcome of a count and not a failure of one.
            </div>
          ) : (
            <VarianceTable variances={review.variances} total={review.amount} />
          )}

          {/* CALLED OUT SEPARATELY, never mixed in with the variances. A line
              nobody reached is not a difference of any size, and presenting it
              as one is how a reviewer comes to believe the count is finished. */}
          {review.uncounted.length > 0 && (
            <div className="note note--warn">
              <strong>{review.uncounted.length} line{review.uncounted.length > 1 ? 's were' : ' was'} never
              counted.</strong> Nobody reached{' '}
              {review.uncounted.slice(0, 6).map((l) => l.code).join(', ')}
              {review.uncounted.length > 6 ? `, and ${review.uncounted.length - 6} more` : ''}.
              These are blank, which is not the same as zero — the books still say what they said.
            </div>
          )}

          {review.journal.length > 0 && (
            <div className="scroll-x">
              <table className="ledger">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>Code</th>
                    <th>Account</th>
                    <th>Description</th>
                    <th className="num" style={{ width: 130 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {review.journal.map((j, i) => (
                    <tr key={`${j.accountId}-${i}`}>
                      <td className="code figure">{j.code}</td>
                      <td>{j.name}</td>
                      <td style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{j.description}</td>
                      <td className="num"><Money value={j.amount} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="controls" style={{ marginTop: 16 }}>
            <label className="field" style={{ minWidth: 300 }}>
              <span>Lines nobody counted</span>
              <select value={uncounted} onChange={(e) => setUncounted(e.target.value)}>
                <option value="refuse">Refuse to post until every line is counted</option>
                <option value="leave">Leave them alone — the books keep what they say</option>
              </select>
            </label>
            <button className="btn" onClick={() => onPost({ uncounted, adjustmentAccountId: contra })}
                    disabled={busy}>
              {busy ? 'Posting' : `Post count ${sheet.number}`}
            </button>
          </div>

          {/* There is no third option here and there is not going to be one.
              See rule 3 at the head of this file. */}
          <div className="note">
            There are two choices and only two. A line nobody counted is either a reason not to
            post yet, or something to leave exactly as the books have it. It is never a zero:
            writing off everything nobody reached is how a storeroom's worth of stock disappears
            at six o'clock on a Friday.
          </div>
        </>
      )}
    </div>
  );
}

/**
 * What a count found, line by line.
 *
 * Shown to a reviewer only — this is reached from the variance route, which is
 * gated on `stock.count.post`. A counter never sees it, and that is the whole
 * point of the blind sheet.
 */
function VarianceTable({ variances, total }) {
  return (
    <div className="scroll-x">
      <table className="ledger">
        <thead>
          <tr>
            <th style={{ width: 60 }}>#</th>
            <th style={{ width: 110 }}>Code</th>
            <th>Item</th>
            <th style={{ width: 140 }}>Serial</th>
            <th className="num" style={{ width: 100 }}>Expected</th>
            <th className="num" style={{ width: 100 }}>Counted</th>
            <th className="num" style={{ width: 100 }}>Difference</th>
            <th className="num" style={{ width: 130 }}>Cost</th>
          </tr>
        </thead>
        <tbody>
          {variances.map((v) => (
            <tr key={v.lineId}>
              <td className="figure" style={{ color: 'var(--ink-faint)' }}>{v.lineNo}</td>
              <td className="code figure">{v.code}</td>
              <td>{v.name}</td>
              <td className="figure">{v.serialNo ?? '\u2014'}</td>
              <td className="num figure">{formatQuantity(v.expectedQty)}</td>
              <td className="num figure">{formatQuantity(v.countedQty)}</td>
              <td className={`num figure ${String(v.varianceQty).startsWith('-') ? 'neg' : ''}`}>
                {formatQuantity(v.varianceQty)}
              </td>
              <td className="num"><Money value={v.amount} /></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={7}>Total difference</td>
            <td className="num"><Money value={total} /></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** What a post actually did — which may legitimately be "no journal at all". */
function Posted({ result }) {
  return (
    <div className="ok" style={{ marginTop: 18 }}>
      <strong>Count {result.number} posted.</strong>{' '}
      {result.entryId ? (
        <>The difference of {money(result.amount)} was written to journal {result.entryNo}.</>
      ) : (
        // A clean count is the most common outcome and it produces NO entry.
        // Nothing here may assume one: the sheet is numbered and sealed, and
        // there was simply nothing to account for.
        <>There was nothing to account for, so the sheet is numbered and sealed with no journal
          behind it. That is what a count that agrees with the books looks like.</>
      )}
    </div>
  );
}

/** A sheet already posted: its variances are still readable, and may have been reversed. */
function PostedSheet({ takeId, sheet }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/api/stock-takes/${takeId}/variances`)
      .then(setData).catch((e) => setErr(e.message));
  }, [takeId]);

  if (err) return <div className="error">{err}</div>;
  if (!data) return null;

  return (
    <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--rule-strong)' }}>
      <h2 className="display" style={{ fontSize: 20, margin: '0 0 10px' }}>What this count posted</h2>
      {/* A POSTED SHEET'S VARIANCES ARE STILL READABLE, and the reversal object
          is why this is not decoration: a sheet whose journal has since been
          undone must not look like one that stands. */}
      {data.reversal && <ReversalNotice reversal={data.reversal} />}
      <div className="stat-row">
        <div className="stat">
          <div className="stat__label">Sheet</div>
          <div className="stat__value">{sheet.number}</div>
        </div>
        <div className="stat">
          <div className="stat__label">Lines with a difference</div>
          <div className="stat__value">{data.variances.length}</div>
        </div>
        <div className="stat">
          <div className="stat__label">Difference</div>
          <div className="stat__value">{money(data.amount)}</div>
        </div>
        <div className="stat">
          <div className="stat__label">Journal</div>
          {/* A count that agreed on every line is posted, numbered and sealed
              WITH NO JOURNAL. That is the most common outcome, not a failure. */}
          <div className="stat__value">
            {sheet.entryId ? 'Posted' : 'None — the count agreed'}
          </div>
        </div>
      </div>
      {data.variances.length > 0 && (
        <VarianceTable variances={data.variances} total={data.amount} />
      )}
      {data.uncounted.length > 0 && (
        <div className="note">
          {data.uncounted.length} line{data.uncounted.length > 1 ? 's on this sheet were' : ' on this sheet was'}{' '}
          never counted, and {data.uncounted.length > 1 ? 'were' : 'was'} left exactly as the books
          had {data.uncounted.length > 1 ? 'them' : 'it'}.
        </div>
      )}
    </div>
  );
}

/**
 * A posted count whose journal has since been reversed.
 *
 * Reversing the entry is the sanctioned way to correct a posted count, so this
 * is an ordinary state and not an exotic one. The figures above it are still
 * what the count POSTED — that is what the route is for — and this is what stops
 * a reader taking them for what the books say today. A sheet whose effect was
 * undone must not look like one that stands.
 */
function ReversalNotice({ reversal }) {
  return (
    <div className="note note--warn">
      <strong>This count's journal has been reversed.</strong> The figures below are what the count
      posted, not what the books say now: journal {reversal.entryNo} undid it on{' '}
      {reversal.reversedOn}. Reversing the entry is the sanctioned way to correct a posted count,
      so this is an ordinary state — but a sheet whose effect was undone must not be read as one
      that stands.
    </div>
  );
}
